"use client"

import axios from "axios"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ROUTES } from "../../../routes/routeKeys"
import Delete from "../../../assets/svgs/Recycle Bin.svg"
import useAuth from "../../../hooks/useAuth"
import { BASE_URL } from "../../../utils/Element"
import Loader from "../../../components/Loader"
import { FiInbox } from "react-icons/fi"

const statusArray = {
  0: "farm_club",           // ← NEW: Farm Club status
  1: "new_lead",
  7: "showing_property",
  2: "buyer_making_offers",
  3: "executed_loi",
  4: "under_contract",
}

const StageDashboard = ({ activeTab, selectedUser }) => {
  const [loading, setLoading] = useState(true)
  const [config] = useAuth()
  const [categories, setCategories] = useState([
    { key: 0, title: "Farm Club", pipelines: [] },           // ← NEW COLUMN
    { key: 1, title: "Qualified Prospect", pipelines: [] },
    { key: 7, title: "Showing Properties", pipelines: [] },
    { key: 2, title: "Buyer Making Offers", pipelines: [] },
    { key: 3, title: "Executed LOI", pipelines: [] },
    { key: 4, title: "Under Contract", pipelines: [] },
  ])

  const [draggedPipelineId, setDraggedPipelineId] = useState(null)

  // Update Pipeline Column Order
  const updatePipelineColumnOrder = async (pipelines) => {
    const columnUpdatePayload = pipelines.map((item, index) => ({
      id: item.id,
      column_no: index,
    }))

    try {
      await axios.put(
        `${BASE_URL}/pipeline-column-update`,
        JSON.stringify(columnUpdatePayload),
        {
          ...config,
          headers: { ...config.headers, "Content-Type": "application/json" },
        }
      )
    } catch (error) {
      console.error("Error updating pipeline column order:", error)
      throw error
    }
  }

  // DELETE PIPELINE
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/delete-pipeline/${id}`, config)
      fetchPipelineStages()
    } catch (error) {
      console.error("Error deleting pipeline:", error)
    }
  }

  const fetchPipelineStages = async () => {
    try {
      setLoading(true)
      let url = `${BASE_URL}/dashboard-pipeline-stage-acquisition?active_tab=${activeTab}`

      if (selectedUser && selectedUser.value) {
        url += `&show_user_id=${selectedUser.value}`
      }

      const response = await axios.get(url, config)
      const pipelineData = response.data.pipeline_stage?.pipeline_status_acquisition || {}

      const formattedCategories = categories.map((cat) => {
        const statusKey = statusArray[cat.key]
        return {
          ...cat,
          pipelines: pipelineData[statusKey]
            ? pipelineData[statusKey].map((item) => ({
                id: item.id,
                name: item.contact_name,
                contact_id: item.contact_id,
                lastContacted: item.last_contact_update_date,
                potentialgcl: item.potential_gcl_format,
                advisorcommission: item.potential_advisor_commission_format,
                pipeline: item.associate_name,
              }))
            : [],
        }
      })

      setCategories(formattedCategories)
    } catch (error) {
      console.error("Error fetching pipelines:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPipelineStages()
  }, [activeTab, selectedUser])

  // ====================== DRAG & DROP ======================
  const onDragStart = (e, pipelineId, fromCategoryIndex, pipelineIndex) => {
    setDraggedPipelineId(pipelineId)
    e.dataTransfer.setData("pipelineId", pipelineId)
    e.dataTransfer.setData("fromCategory", fromCategoryIndex)
    e.dataTransfer.setData("pipelineIndex", pipelineIndex)
  }

  const onDragEnd = () => {
    setDraggedPipelineId(null)
  }

  const onDragOver = (e) => {
    e.preventDefault()
  }

  const onDrop = (e, toCategoryIndex) => {
    e.preventDefault()

    const pipelineId = parseInt(e.dataTransfer.getData("pipelineId"), 10)
    const fromCategoryIndex = parseInt(e.dataTransfer.getData("fromCategory"), 10)

    if (isNaN(pipelineId) || isNaN(fromCategoryIndex)) return

    const dropY = e.clientY
    const container = e.currentTarget.querySelector(".pipeline-container")
    if (!container) return

    const children = Array.from(container.children)
    let insertIndex = children.length

    for (let i = 0; i < children.length; i++) {
      const box = children[i].getBoundingClientRect()
      if (dropY < box.top + box.height / 2) {
        insertIndex = i
        break
      }
    }

    // Update UI State
    setCategories((prev) => {
      const newCategories = prev.map((cat) => ({
        ...cat,
        pipelines: [...cat.pipelines],
      }))

      let movedPipeline = null
      for (let cat of newCategories) {
        const idx = cat.pipelines.findIndex((p) => p.id === pipelineId)
        if (idx !== -1) {
          movedPipeline = cat.pipelines.splice(idx, 1)[0]
          break
        }
      }

      if (!movedPipeline) return prev

      newCategories[toCategoryIndex].pipelines.splice(insertIndex, 0, movedPipeline)

      // Persist column order of target column
      const targetPipelines = newCategories[toCategoryIndex].pipelines
      updatePipelineColumnOrder(targetPipelines).catch(console.error)

      return newCategories
    })

    setDraggedPipelineId(null)

    // Update status only when moving between different columns
    if (fromCategoryIndex !== toCategoryIndex) {
      const newStatus = statusArray[categories[toCategoryIndex].key]
      axios
        .put(`${BASE_URL}/update-pipeline-status/${pipelineId}`, { status: newStatus }, config)
        .catch((err) => {
          console.error("Backend status update failed", err)
          fetchPipelineStages() // rollback
        })
    }
  }

  // Delete Drop Zone
  const onDeleteDrop = async (e) => {
    e.preventDefault()
    const pipelineId = parseInt(e.dataTransfer.getData("pipelineId"), 10)
    if (!pipelineId) return

    try {
      await axios.delete(`${BASE_URL}/delete-pipeline/${pipelineId}`, config)
      fetchPipelineStages()
    } catch (err) {
      console.error("Delete failed", err)
    } finally {
      setDraggedPipelineId(null)
    }
  }

  return (
    <div className="flex flex-col w-full bg-gray-100 p-4 overflow-hidden relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
          <Loader />
        </div>
      )}
      {/* COLUMNS */}
      <div className="flex w-full space-x-3 flex-1 mb-4 overflow-y-auto">
        {categories.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className="flex-1 flex flex-col"
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, categoryIndex)}
          >
            <div className="green-bg-H text-white px-2 py-1 rounded-t-md text-center text-sm font-medium">
              {category.title}
            </div>

            <div className="bg-white flex-1 overflow-y-auto max-h-[520px] min-h-[100px] border border-gray-200 rounded-b-md pipeline-container flex flex-col">
              {category.pipelines.length > 0 ? (
                category.pipelines.map((pipeline, pipelineIndex) => (
                  <div
                    key={pipeline.id}
                    className={`p-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-sm ${
                      draggedPipelineId === pipeline.id ? "opacity-50" : ""
                    }`}
                    draggable
                    onDragStart={(e) =>
                      onDragStart(e, pipeline.id, categoryIndex, pipelineIndex)
                    }
                    onDragEnd={onDragEnd}
                  >
                    <p className="font-medium text-sm">
                      <Link to={ROUTES.CONTACT_DETAILS.replace(":id", pipeline.contact_id)}>{pipeline.name}</Link>
                    </p>
                    <p className="text-xs text-gray-500">
                      Last Contacted: {pipeline.lastContacted || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      Potential GCI: ${pipeline.potentialgcl || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      Potential Advisor Commission: ${pipeline.advisorcommission || "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      Pipeline: {pipeline.pipeline || "—"}
                    </p>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 h-full py-8 text-gray-400">
                  <FiInbox className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-xs font-medium text-center opacity-70">No data</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* DELETE DROP AREA */}
      <div
        className="w-full flex justify-center py-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDeleteDrop}
      >
        <button className="flex items-center gap-2 border border-red-400 text-red-500 px-4 py-1.5 rounded-full bg-white hover:bg-red-50 transition-all text-sm">
          <img src={Delete} alt="delete" className="w-4 h-4" />
          Remove from Pipeline
        </button>
      </div>
    </div>
  )
}

export default StageDashboard